import { useRef, useState, type ReactElement, type RefObject } from "react";
import { Link, useLocation } from "react-router";

import type { FieldErrors } from "../../api/client.js";
import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";

import { ActivitySearchForm, type ActivityConditions, type ActivityKind } from "./activity-search-form.js";
import { CenterBackLink } from "./center-back-link.js";
import { CsvDownload, csvLimitLabel } from "./csv-download.js";
import { Pager } from "./pager.js";
import { adminCenterApi, adminCenterUrls } from "./urls.js";

/** アクティビティログの 1 行（<b>文言はサーバが作る</b>——日時・種別の表示名）。 */
export interface CenterActivityRow {
    readonly occurredLabel: string;
    readonly kind: string;
    readonly kindLabel: string;
    readonly actorEmail: string | null;
    readonly actorName: string | null;
    readonly targetEmail: string | null;
    readonly targetName: string | null;
    readonly tenantId: number | null;
    readonly tenantTitle: string | null;
    readonly detail: string | null;
    /** 関連するメール（無ければ空）。<b>中身はここに出さない</b>——送ったメールの 1 通の画面で開く。 */
    readonly mails: readonly CenterActivityMail[];
}

/** アクティビティに関連する送ったメール（件名と宛先だけ）。 */
export interface CenterActivityMail {
    readonly mailLogId: number;
    readonly subject: string;
    readonly to: string;
}

/** アクティビティログを探す画面（<c>GET /api/admin-center/activity</c>）。行は新しい順にサーバが並べる。 */
export interface CenterActivity extends ActivityConditions {
    /** 契約で絞ったときの、その契約の名前（サーバが引く）。絞っていない・無い契約なら null。 */
    readonly tenantTitle: string | null;
    readonly rows: readonly CenterActivityRow[];
    readonly kinds: readonly ActivityKind[];
    readonly page: number;
    readonly pageCount: number;
    readonly matchingCount: number;
    /** CSV に書き出す行の上限（サーバが決める。新しい順にここまで）。 */
    readonly csvLimit: number;
}

const noErrors: FieldErrors = {};

/** 人の欄（名前と、その下にメールアドレス）。どちらも無ければ空にする。 */
function Person({ name, email }: { readonly name: string | null; readonly email: string | null }): ReactElement {
    return (
        <>
            {name === null ? null : <span className="block font-medium text-ink-800">{name}</span>}
            {email === null ? null : <span className="block text-xs text-slate-500">{email}</span>}
        </>
    );
}

/**
 * 関連するメール。<b>1 通ごとに「メールを見る」</b>（送ったメールの 1 通の画面）と件名を並べる——
 * 何通あっても件名で見分けられる。宛先は title に添える。本文はここに出さない。
 */
function RelatedMails({ mails }: { readonly mails: readonly CenterActivityMail[] }): ReactElement | null {
    if (mails.length === 0) {
        return null;
    }
    return (
        <ul>
            {mails.map((mail) => (
                <li key={mail.mailLogId} className="mt-1 flex flex-wrap items-center gap-2">
                    <Link
                        to={adminCenterUrls.mail(mail.mailLogId)} className="btn-secondary btn-sm"
                        title={`${mail.subject}（${mail.to}）`}
                    >
                        メールを見る
                    </Link>
                    <span className="text-xs text-slate-500">{mail.subject}</span>
                </li>
            ))}
        </ul>
    );
}

function ActivityRows({ rows }: { readonly rows: readonly CenterActivityRow[] }): ReactElement {
    return (
        <tbody>
            {rows.map((row, index) => (
                // 行を指す番号はサーバが返さない（並びは新しい順で決まる）
                <tr key={index}>
                    <td className="whitespace-nowrap text-slate-500">{row.occurredLabel}</td>
                    <td className="whitespace-nowrap">{row.kindLabel}</td>
                    <td><Person name={row.actorName} email={row.actorEmail} /></td>
                    <td><Person name={row.targetName} email={row.targetEmail} /></td>
                    <td>
                        {row.tenantId === null
                            ? null
                            : (
                                <>
                                    {row.tenantTitle === null ? null : <span className="block">{row.tenantTitle}</span>}
                                    <Link to={adminCenterUrls.tenant(row.tenantId)} className="btn-secondary btn-sm">{row.tenantId}</Link>
                                </>
                            )}
                    </td>
                    <td className="text-sm">
                        {row.detail}
                        <RelatedMails mails={row.mails} />
                    </td>
                </tr>
            ))}
        </tbody>
    );
}

/** 契約で絞っていることを出す（契約の名前はサーバが返す。無い契約ならその旨）。 */
function TenantScope({ found }: { readonly found: CenterActivity }): ReactElement | null {
    if (found.tenantId === null) {
        return null;
    }
    const id = String(found.tenantId);
    return (
        <p className="mb-3 text-sm text-slate-500" data-activity-tenant="">
            {found.tenantTitle === null
                ? `契約 ID ${id} に絞っています（この番号の契約は見つかりません）。`
                : `契約「${found.tenantTitle}」（契約 ID ${id}）に絞っています。`}
        </p>
    );
}

function Results({ found, top }: {
    readonly found: CenterActivity;
    readonly top: RefObject<HTMLDivElement | null>;
}): ReactElement {
    if (found.rows.length === 0) {
        return <div className="empty-state text-sm text-slate-500">当たる記録がありません。</div>;
    }

    return (
        <>
            <CsvDownload
                filePrefix="activity"
                url={adminCenterApi.activityCsv({
                    from: found.from, to: found.to, kind: found.kind, keyword: found.keyword, tenantId: found.tenantId,
                })}
                summary={`${String(found.matchingCount)} 件`}
                help={`CSV には、ページに関係なく当たる記録を新しい順に ${csvLimitLabel(found.csvLimit)} 件まで書き出します。`}
                warning={found.matchingCount > found.csvLimit
                    ? `当たりが ${csvLimitLabel(found.csvLimit)} 件を超えています。CSV には新しい順に ${csvLimitLabel(found.csvLimit)} 件までを書き出します。期間で絞ってください。`
                    : null}
            />
            <div className="table-card" ref={top}>
                <div className="table-scroll">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th scope="col">日時</th>
                                <th scope="col">種別</th>
                                <th scope="col">操作した人</th>
                                <th scope="col">対象</th>
                                <th scope="col">契約</th>
                                <th scope="col">補足</th>
                            </tr>
                        </thead>
                        <ActivityRows rows={found.rows} />
                    </table>
                </div>
            </div>
            <Pager
                page={found.page}
                pageCount={found.pageCount}
                hasNext={found.page < found.pageCount}
                total={`全 ${String(found.matchingCount)} 件`}
                hrefOf={(page) => adminCenterUrls.activity({
                    from: found.from, to: found.to, kind: found.kind, keyword: found.keyword, tenantId: found.tenantId, page,
                })}
                top={top}
                marker={{ "data-activity-pager": "" }}
            />
        </>
    );
}

/** 断られた（400）ときの欄の値。<b>打った条件をそのまま残す</b>（直して探し直せる）。 */
const conditionsOf = (search: string): ActivityConditions => {
    const query = new URLSearchParams(search);
    const raw = query.get("tenantId")?.trim() ?? "";
    const tenantId = Math.trunc(Number(raw));
    return {
        from: query.get("from"), to: query.get("to"), kind: query.get("kind"), keyword: query.get("keyword"),
        tenantId: raw === "" || Number.isNaN(tenantId) ? null : tenantId,
    };
};

/**
 * アクティビティログ。<b>条件は URL のクエリ</b>（<c>from</c>・<c>to</c>・<c>kind</c>・<c>keyword</c>・<c>tenantId</c>・<c>page</c>）。
 * <c>tenantId</c> は契約 1 件の画面の入口から渡ってくる（その契約に絞る）。
 * 開けるかはサーバが決める（運営の権限のどれか。無ければ 404 →「見つからない」）。
 *
 * <b>期間の誤りは 400（ValidationProblem）で断られる。</b>そのときも窓は残し、断りを欄の下（欄に結び付かないものは先頭）に出す。
 * 種別の選択肢は最後に届いたものを使う。
 */
export function ActivityPage(): ReactElement | null {
    const { search } = useLocation();
    const data = useApiData<CenterActivity>(adminCenterApi.activity(search));
    usePageMeta(titleFor(data, "アクティビティログ"));
    const top = useRef<HTMLDivElement | null>(null);
    const [kinds, setKinds] = useState<readonly ActivityKind[]>([]);
    if (data.state === "ready" && data.value.kinds !== kinds) {
        setKinds(data.value.kinds);
    }

    const rejected = data.state === "failed" && data.status === 400 && data.errors !== undefined;
    if (data.state !== "ready" && !rejected) {
        return <ApiState data={data} what="アクティビティログ" />;
    }

    return (
        <div className="page max-w-5xl">
            <CenterBackLink />
            <div className="page-header">
                <div>
                    <h1 className="page-title">アクティビティログ</h1>
                    <p className="page-subtitle">
                        誰が・いつ・何をしたかの記録を探します。
                        <strong className="text-ink-800">期間・種別・操作した人や対象のメールアドレスか名前・契約 ID</strong>で絞り込めます。
                    </p>
                </div>
            </div>

            {/* 窓は断られたときも同じ位置に描く（作り直すとフォーカスが外れる。docs/01 §8） */}
            {data.state === "ready"
                ? <ActivitySearchForm conditions={data.value} kinds={data.value.kinds} errors={noErrors} />
                : (
                    <ActivitySearchForm
                        conditions={conditionsOf(search)} kinds={kinds}
                        errors={data.errors ?? noErrors}
                    />
                )}
            {data.state === "ready" ? <TenantScope found={data.value} /> : null}
            {data.state === "ready" ? <Results found={data.value} top={top} /> : null}
        </div>
    );
}
